import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { adminAPI } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/shared/Logo";
import { SEODashboard } from "@/components/SEODashboard";
import ProspectionCRM from "@/components/ProspectionCRM";
import {
  Users, Hotel, Briefcase, TrendingUp, CreditCard, Crown, LogOut,
  BarChart3, Activity, UserCheck, Ban, CheckCircle2, ShieldCheck, Clock, AlertTriangle, Globe, Mail, Phone, X, Funnel, Target
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

const CHART_COLORS = ["#D4A853", "#0A1628", "#3B82F6", "#10B981", "#EF4444"];

function AcquisitionFunnel({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");

  const loadFunnel = async () => {
    setLoading(true);
    try {
      const params = {};
      if (utmSource) params.utm_source = utmSource;
      if (utmMedium) params.utm_medium = utmMedium;
      if (utmCampaign) params.utm_campaign = utmCampaign;
      const res = await adminAPI.getAcquisitionFunnel(params);
      setData(res.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { loadFunnel(); }, [utmSource, utmMedium, utmCampaign]);

  if (loading || !data) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;

  const f = data.funnel;
  const db = data.db_totals;
  const steps = [
    { label: t("admin.funnel_landing_views"), value: f.landing_views, color: "bg-sky-500" },
    { label: t("admin.funnel_signup_started"), value: f.signup_started, color: "bg-indigo-500" },
    { label: t("admin.funnel_signup_completed"), value: f.signup_completed, color: "bg-violet-500" },
    { label: t("admin.funnel_onboarding"), value: f.onboarding_completed, color: "bg-amber-500" },
    { label: t("admin.funnel_jobs_published"), value: f.jobs_published, color: "bg-teal-500" },
  ];
  const maxVal = Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="space-y-6" data-testid="acquisition-funnel">
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-sans text-lg flex items-center gap-2">
            <Funnel className="w-5 h-5 text-gold-500" /> {t("admin.funnel_title")}
          </CardTitle>
          <p className="text-xs text-slate-400 font-body mt-1">{t("admin.funnel_tracked_only")}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Input placeholder={t("admin.funnel_filter_source")} value={utmSource} onChange={e => setUtmSource(e.target.value)} className="w-40 h-8 text-xs font-body" data-testid="funnel-filter-source" />
            <Input placeholder={t("admin.funnel_filter_medium")} value={utmMedium} onChange={e => setUtmMedium(e.target.value)} className="w-40 h-8 text-xs font-body" data-testid="funnel-filter-medium" />
            <Input placeholder={t("admin.funnel_filter_campaign")} value={utmCampaign} onChange={e => setUtmCampaign(e.target.value)} className="w-40 h-8 text-xs font-body" data-testid="funnel-filter-campaign" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {steps.map((step, i) => {
              const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
              const prevVal = i > 0 ? steps[i - 1].value : null;
              const dropRate = prevVal && prevVal > 0 ? Math.round(((prevVal - step.value) / prevVal) * 100) : null;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                  <div className="w-36 md:w-44 text-xs font-body text-slate-600 text-right shrink-0">{step.label}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
                    <div className={`${step.color} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold font-body text-slate-700">{step.value}</span>
                  </div>
                  {dropRate !== null && dropRate > 0 && (
                    <span className="text-[10px] font-body text-red-400 w-10 shrink-0">-{dropRate}%</span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* DB context totals + UTM + Recent in a 3-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Historical DB Totals */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="font-sans text-sm">{t("admin.funnel_db_totals")}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[
              [t("admin.funnel_db_hotels"), db.total_hotels],
              [t("admin.funnel_db_onboarded"), db.onboarded],
              [t("admin.funnel_db_verified"), db.verified],
              [t("admin.funnel_db_with_jobs"), db.with_active_jobs],
              [t("admin.funnel_active_jobs"), db.active_jobs],
            ].map(([label, val], i) => (
              <div key={i} className="flex items-center justify-between text-sm font-body">
                <span className="text-slate-600">{label}</span>
                <span className="font-semibold text-slate-800">{val}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* UTM Breakdown */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="font-sans text-sm">{t("admin.funnel_utm_breakdown")}</CardTitle></CardHeader>
          <CardContent>
            {data.utm_breakdown.length === 0 ? (
              <p className="text-xs text-slate-400 font-body">{t("admin.funnel_no_utm")}</p>
            ) : (
              <div className="space-y-2">
                {data.utm_breakdown.map((u, i) => (
                  <div key={i} className="flex items-center justify-between text-sm font-body">
                    <span className="text-slate-700">{u.source}</span>
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200">{u.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Hotels */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="font-sans text-sm">{t("admin.funnel_recent")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.recent_hotels.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs font-body border-b border-slate-50 pb-2 last:border-0">
                  <div className="min-w-0">
                    <p className="text-slate-800 font-medium truncate">{h.hotel_name || h.email}</p>
                    <div className="flex gap-1.5 mt-0.5 flex-wrap">
                      {h.verification_status === "verified" && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">Verified</Badge>}
                      {h.verification_status === "pending" && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Pending</Badge>}
                      {h.onboarding_completed && <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] px-1.5 py-0">Onboarded</Badge>}
                      {h.job_count > 0 && <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0">{h.job_count} jobs</Badge>}
                      {h.utm?.utm_source && <Badge className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] px-1.5 py-0">{h.utm.utm_source}</Badge>}
                    </div>
                  </div>
                  <span className="text-slate-400 shrink-0 ml-2">{h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = "text-gold-500" }) {
  return (
    <div className="stat-card" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-xs text-slate-400 font-body uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-sans font-bold text-2xl text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 font-body mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t, tc } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [subscriptions, setSubscriptions] = useState([]);
  const [hires, setHires] = useState({ hires: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [verificationHotels, setVerificationHotels] = useState({ hotels: [], total: 0 });
  const [verificationFilter, setVerificationFilter] = useState("pending");
  const [rejectingHotel, setRejectingHotel] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const results = await Promise.allSettled([
        adminAPI.getMetrics(), adminAPI.getRevenue(), adminAPI.getUsers({}),
        adminAPI.getSubscriptions(), adminAPI.getHires({}), adminAPI.getHotelsForVerification("pending")
      ]);
      if (results[0].status === "fulfilled") setMetrics(results[0].value.data);
      if (results[1].status === "fulfilled") setRevenue(results[1].value.data);
      if (results[2].status === "fulfilled") setUsers(results[2].value.data);
      if (results[3].status === "fulfilled") setSubscriptions(results[3].value.data);
      if (results[4].status === "fulfilled") setHires(results[4].value.data);
      if (results[5].status === "fulfilled") setVerificationHotels(results[5].value.data);
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  const loadUsers = async (role) => {
    setRoleFilter(role);
    try {
      const params = role !== "all" ? { role } : {};
      const res = await adminAPI.getUsers(params);
      setUsers(res.data);
    } catch { }
  };

  const toggleUser = async (userId) => {
    try {
      await adminAPI.toggleUser(userId);
      loadUsers(roleFilter);
      toast.success("OK");
    } catch { toast.error("Error"); }
  };

  const loadVerificationHotels = async (status) => {
    setVerificationFilter(status);
    try {
      const res = await adminAPI.getHotelsForVerification(status);
      setVerificationHotels(res.data);
    } catch { }
  };

  const handleVerifyHotel = async (userId) => {
    try {
      await adminAPI.verifyHotel(userId);
      toast.success("OK");
      loadVerificationHotels(verificationFilter);
      loadAll();
    } catch { toast.error("Error"); }
  };

  const handleRejectHotel = async () => {
    if (!rejectingHotel) return;
    try {
      await adminAPI.rejectHotel(rejectingHotel, rejectReason);
      toast.success("OK");
      setRejectingHotel(null);
      setRejectReason("");
      loadVerificationHotels(verificationFilter);
      loadAll();
    } catch { toast.error("Error"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const pieData = [
    { name: t("admin.sub_revenue"), value: metrics?.subscription_revenue || 0 },
    { name: t("common.premium"), value: metrics?.premium_revenue || 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-navy-900 border-b border-navy-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <Logo size="md" className="text-white" />
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30 font-body ml-2">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <span className="text-sm text-slate-300 font-body">{user?.name}</span>
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={logout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-sans font-bold text-2xl text-slate-900 mb-6">{t("admin.title")}</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard icon={Users} label={t("admin.candidates")} value={metrics?.total_candidates || 0} />
          <MetricCard icon={Hotel} label={t("admin.hotels")} value={metrics?.total_hotels || 0} />
          <MetricCard icon={UserCheck} label={t("admin.hires")} value={metrics?.total_hires || 0} />
          <MetricCard icon={TrendingUp} label={t("admin.total_revenue")} value={`${(metrics?.total_revenue || 0).toFixed(0)} EUR`} color="text-emerald-500" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard icon={Crown} label={t("admin.active_subs")} value={metrics?.active_subscriptions || 0} />
          <MetricCard icon={Activity} label={t("admin.available_now")} value={metrics?.available_now_candidates || 0} />
          <MetricCard icon={ShieldCheck} label={t("admin.verified_hotels")} value={metrics?.verified_hotels || 0} color="text-emerald-500" />
          <MetricCard icon={Clock} label={t("admin.pending_hotels")} value={metrics?.pending_verification || 0} color="text-amber-500" />
        </div>

        <Tabs defaultValue="verification" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="verification" className="font-body" data-testid="tab-verification">
              {t("admin.verification")} {(metrics?.pending_verification || 0) > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0">{metrics.pending_verification}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="revenue" className="font-body" data-testid="tab-revenue">{t("admin.revenue")}</TabsTrigger>
            <TabsTrigger value="users" className="font-body" data-testid="tab-users">{t("admin.users")}</TabsTrigger>
            <TabsTrigger value="hires" className="font-body" data-testid="tab-hires">{t("admin.hires")}</TabsTrigger>
            <TabsTrigger value="subscriptions" className="font-body" data-testid="tab-subscriptions">{t("admin.subscriptions")}</TabsTrigger>
            <TabsTrigger value="seo" className="font-body" data-testid="tab-seo"><Globe className="w-3.5 h-3.5 mr-1" />SEO</TabsTrigger>
            <TabsTrigger value="funnel" className="font-body" data-testid="tab-funnel"><Funnel className="w-3.5 h-3.5 mr-1" />{t("admin.funnel")}</TabsTrigger>
            <TabsTrigger value="prospection" className="font-body" data-testid="tab-prospection"><Target className="w-3.5 h-3.5 mr-1" />Prospection</TabsTrigger>
          </TabsList>

          <TabsContent value="verification">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-sans text-lg flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-gold-500" /> {t("admin.verification")} ({verificationHotels.total})
                  </CardTitle>
                  <Select value={verificationFilter} onValueChange={loadVerificationHotels}>
                    <SelectTrigger className="w-40 font-body" data-testid="verification-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">{t("admin.status_pending")}</SelectItem>
                      <SelectItem value="verified">{t("admin.status_verified")}</SelectItem>
                      <SelectItem value="rejected">{t("admin.status_rejected")}</SelectItem>
                      <SelectItem value="all">{t("admin.status_all")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(verificationHotels.hotels || []).map(h => (
                    <div key={h.user_id} className="border border-slate-200 rounded-lg p-4" data-testid={`verification-hotel-${h.user_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-sans font-semibold text-slate-900">{h.name || "Sin nombre"}</h3>
                            <Badge className={`text-xs font-body ${
                              h.verification_status === "verified" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                              h.verification_status === "rejected" ? "bg-red-100 text-red-700 border-red-200" :
                              h.verification_status === "flagged" ? "bg-orange-100 text-orange-700 border-orange-200" :
                              "bg-amber-100 text-amber-700 border-amber-200"
                            }`}>
                              {h.verification_status === "flagged" ? t("verif.flagged") : h.verification_status}
                            </Badge>
                            {h.is_public_email && (
                              <Badge className="bg-red-50 text-red-600 border-red-200 text-xs font-body">
                                <AlertTriangle className="w-3 h-3 mr-1" />{t("admin.public_email_warning")}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm font-body text-slate-600">
                            <div className="flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              <span>{h.corporate_email || h.owner_email || "-"}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              <span>{h.phone || "-"}</span>
                            </div>
                            {h.website && (
                              <div className="flex items-center gap-1.5">
                                <Globe className="w-3.5 h-3.5 text-slate-400" />
                                <a href={h.website.startsWith("http") ? h.website : `https://${h.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{h.website}</a>
                              </div>
                            )}
                            <div className="text-slate-500">{h.location}, {tc(h.country)}</div>
                          </div>
                          <div className="mt-2 text-xs text-slate-500 font-body">
                            <span>{t("admin.contact_info")}: {h.contact_person || "-"} ({h.contact_title || "-"})</span>
                            <span className="ml-3">{t("hd.subscription")}: <span className="capitalize font-semibold">{h.subscription_plan || "free"}</span></span>
                          </div>
                        </div>
                        {h.verification_status !== "verified" && (
                          <div className="flex gap-2 flex-shrink-0">
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white font-body rounded-sm"
                              onClick={() => handleVerifyHotel(h.user_id)} data-testid={`verify-btn-${h.user_id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t("admin.verify_btn")}
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 font-body rounded-sm"
                              onClick={() => setRejectingHotel(h.user_id)} data-testid={`reject-btn-${h.user_id}`}>
                              <X className="w-3.5 h-3.5 mr-1" />{t("admin.reject_btn")}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!verificationHotels.hotels || verificationHotels.hotels.length === 0) && (
                    <p className="text-center text-slate-400 font-body py-8">{t("admin.no_pending")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
            {/* Reject dialog */}
            <Dialog open={!!rejectingHotel} onOpenChange={(open) => { if (!open) { setRejectingHotel(null); setRejectReason(""); } }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-sans">{t("admin.reject_btn")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-body text-slate-600">{t("admin.reject_reason")}</label>
                    <Input className="mt-1 font-body" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={t("admin.reject_reason_placeholder")} data-testid="reject-reason-input" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" className="font-body" onClick={() => { setRejectingHotel(null); setRejectReason(""); }}>{t("common.cancel")}</Button>
                    <Button className="bg-red-500 hover:bg-red-600 text-white font-body" onClick={handleRejectHotel} data-testid="confirm-reject-btn">
                      {t("admin.reject_btn")}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="revenue">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-sans text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-gold-500" /> {t("admin.monthly_revenue")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(revenue?.monthly_revenue || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={revenue.monthly_revenue}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fontFamily: 'Manrope' }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 12, fontFamily: 'Manrope' }} stroke="#94a3b8" />
                        <Tooltip contentStyle={{ fontFamily: 'Manrope', fontSize: 13, borderRadius: 6 }} />
                        <Bar dataKey="subscriptions" name={t("admin.subscriptions")} fill="#D4A853" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="premium" name={t("common.premium")} fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400 font-body">{t("admin.no_data")}</div>
                  )}
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader><CardTitle className="font-sans text-lg">{t("admin.revenue_dist")}</CardTitle></CardHeader>
                <CardContent>
                  {pieData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {pieData.map((entry, i) => <Cell key={entry.name} fill={CHART_COLORS[i]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontFamily: 'Manrope', fontSize: 13, borderRadius: 6 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-400 font-body">{t("admin.no_data")}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-sans text-lg">{t("admin.users")} ({users.total})</CardTitle>
                  <Select value={roleFilter} onValueChange={loadUsers}>
                    <SelectTrigger className="w-40 font-body" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("admin.all")}</SelectItem>
                      <SelectItem value="candidate">{t("admin.candidates")}</SelectItem>
                      <SelectItem value="hotel">{t("admin.hotels")}</SelectItem>
                      <SelectItem value="admin">{t("admin.admins")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("admin.name")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("auth.email")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("admin.role")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("admin.date")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(users.users || []).map(u => (
                        <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                          <td className="py-3 px-2 text-slate-900">{u.name}</td>
                          <td className="py-3 px-2 text-slate-600">{u.email}</td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className={`font-body text-xs ${
                              u.role === "admin" ? "border-red-200 text-red-600" : u.role === "hotel" ? "border-blue-200 text-blue-600" : "border-emerald-200 text-emerald-600"
                            }`}>{u.role}</Badge>
                          </td>
                          <td className="py-3 px-2 text-slate-500">{u.created_at?.split("T")[0]}</td>
                          <td className="py-3 px-2">
                            {u.role !== "admin" && (
                              <Switch checked={u.active !== false} onCheckedChange={() => toggleUser(u.user_id)}
                                data-testid={`toggle-user-${u.user_id}`} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hires">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="font-sans text-lg">{t("admin.hires")} ({hires.total})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">Hotel</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("admin.candidates")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("hd.position")}</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">Status</th>
                        <th className="text-left py-3 px-2 text-slate-500 font-semibold">{t("admin.date")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(hires.hires || []).map(h => (
                        <tr key={h.hire_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`hire-row-${h.hire_id}`}>
                          <td className="py-3 px-2 text-slate-900">{h.hotel_name}</td>
                          <td className="py-3 px-2 text-slate-600">{h.candidate_name}</td>
                          <td className="py-3 px-2"><Badge variant="outline" className="font-body text-xs">{h.position}</Badge></td>
                          <td className="py-3 px-2"><Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-body text-xs">{h.status || "confirmed"}</Badge></td>
                          <td className="py-3 px-2 text-slate-500">{h.created_at?.split("T")[0]}</td>
                        </tr>
                      ))}
                      {(!hires.hires || hires.hires.length === 0) && (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-400">{t("admin.no_data")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="font-sans text-lg">{t("admin.active_subs_list")} ({subscriptions.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {subscriptions.map((s) => (
                    <div key={s.hotel_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`subscription-${s.hotel_id}`}>
                      <div>
                        <p className="font-sans font-semibold text-slate-900">{s.name}</p>
                        <p className="text-sm text-slate-500 font-body">{s.owner_email} - {s.location}</p>
                      </div>
                      <Badge className="bg-gold-500/10 text-gold-600 border-gold-500/20 font-body capitalize">{s.subscription_plan}</Badge>
                    </div>
                  ))}
                  {subscriptions.length === 0 && <p className="text-center text-slate-400 font-body py-8">{t("admin.no_subs")}</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seo">
            <SEODashboard />
          </TabsContent>

          <TabsContent value="funnel">
            <AcquisitionFunnel t={t} />
          </TabsContent>

          <TabsContent value="prospection">
            <ProspectionCRM />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
